import DOMPurify from 'dompurify'
import { marked } from 'marked'

import type { KnowledgeBaseDocument } from '@/lib/definitions/knowledgeBase'
import {
  resolveKnowledgeBaseAsset,
  resolveKnowledgeBaseHref,
} from '@/lib/misc/knowledgeBase'

export interface KnowledgeBaseHeading {
  id: string
  text: string
}

export interface RenderedKnowledgeBaseDocument {
  title: string
  lead?: string
  html: string
  headings: KnowledgeBaseHeading[]
  readingMinutes: number
}

export const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')

const TITLE_SUFFIX = /\s*[–-]\s*Knowledgebase$/i
const WARNING_CALLOUT = /\b(draft|TBD|warning|important)\b/i
const WORDS_PER_MINUTE = 220

const isHeading = (element: Element | null) =>
  element != null && /^H[1-5]$/.test(element.tagName)

const rewriteLinks = (
  root: HTMLElement,
  path: string,
  documents: KnowledgeBaseDocument[]
) => {
  root.querySelectorAll('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href') ?? ''
    if (/^(https?:|mailto:)/.test(href)) {
      anchor.setAttribute('target', '_blank')
      anchor.setAttribute('rel', 'noopener noreferrer')
      anchor.classList.add('kb-external')
      return
    }
    if (href.startsWith('#')) {
      anchor.setAttribute('data-kb-anchor', href.slice(1))
      anchor.setAttribute('href', href)
      return
    }
    if (/\.md(#|$)/.test(href)) {
      anchor.setAttribute(
        'data-kb-key',
        resolveKnowledgeBaseHref(path, href, documents)
      )
      anchor.setAttribute('href', '#')
    }
  })
}

const rewriteImages = (root: HTMLElement, path: string) => {
  root.querySelectorAll('img').forEach((image) => {
    const src = image.getAttribute('src')
    if (src) image.setAttribute('src', resolveKnowledgeBaseAsset(path, src))
    image.setAttribute('loading', 'lazy')
  })
}

const assignHeadingIds = (root: HTMLElement) => {
  const seen = new Map<string, number>()
  root.querySelectorAll('h2, h3, h4').forEach((heading) => {
    const base = slugifyHeading(heading.textContent ?? '')
    const count = (seen.get(base) ?? 0) + 1
    seen.set(base, count)
    heading.id = count > 1 ? `${base}-${count}` : base
  })
}

const collapseQuestions = (root: HTMLElement) => {
  const document = root.ownerDocument
  root.querySelectorAll('h5').forEach((question) => {
    const details = document.createElement('details')
    details.className = 'kb-qa'
    const summary = document.createElement('summary')
    summary.innerHTML = question.innerHTML
    const answer = document.createElement('div')
    answer.className = 'kb-qa-answer'
    let sibling = question.nextElementSibling
    while (sibling && !isHeading(sibling)) {
      const next = sibling.nextElementSibling
      answer.appendChild(sibling)
      sibling = next
    }
    details.append(summary, answer)
    question.replaceWith(details)
  })
}

const decorateBlocks = (root: HTMLElement) => {
  const document = root.ownerDocument
  root.querySelectorAll('blockquote').forEach((quote) => {
    quote.classList.add(
      WARNING_CALLOUT.test(quote.textContent ?? '')
        ? 'kb-callout-warning'
        : 'kb-callout'
    )
  })
  root.querySelectorAll('table').forEach((table) => {
    const wrapper = document.createElement('div')
    wrapper.className = 'kb-table'
    table.replaceWith(wrapper)
    wrapper.appendChild(table)
  })
}

const extractTitle = (root: HTMLElement, fallback: string) => {
  const heading = root.querySelector('h1')
  if (!heading) return fallback
  const title = (heading.textContent ?? '').replace(TITLE_SUFFIX, '').trim()
  heading.remove()
  return title || fallback
}

const extractLead = (root: HTMLElement) => {
  const first = root.firstElementChild
  if (first?.tagName !== 'P' || first.querySelector('img')) return undefined
  const lead = first.innerHTML
  first.remove()
  return lead
}

export const renderKnowledgeBaseDocument = (
  markdown: string,
  path: string,
  fallbackTitle: string,
  documents: KnowledgeBaseDocument[]
): RenderedKnowledgeBaseDocument => {
  const parsed = marked.parse(markdown, { async: false, gfm: true })
  const container = document.createElement('div')
  container.innerHTML = DOMPurify.sanitize(parsed)

  container.querySelectorAll('hr').forEach((rule) => rule.remove())
  const title = extractTitle(container, fallbackTitle)
  const lead = extractLead(container)

  rewriteLinks(container, path, documents)
  rewriteImages(container, path)
  assignHeadingIds(container)
  collapseQuestions(container)
  decorateBlocks(container)

  const headings = Array.from(container.querySelectorAll('h2')).map(
    (heading) => ({ id: heading.id, text: heading.textContent?.trim() ?? '' })
  )
  const words = (container.textContent ?? '').split(/\s+/).filter(Boolean)

  return {
    title,
    lead,
    html: container.innerHTML,
    headings,
    readingMinutes: Math.max(1, Math.round(words.length / WORDS_PER_MINUTE)),
  }
}

export interface KnowledgeBaseIndexEntry {
  document: KnowledgeBaseDocument
  heading?: string
  anchor?: string
  text: string
}

export interface KnowledgeBaseSearchResult {
  key: string
  document: KnowledgeBaseDocument
  heading?: string
  snippet: string
  terms: string[]
}

const toPlainText = (markdown: string) =>
  markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[*_`>|#]+/g, ' ')
    .replace(/^\s*-{3,}\s*$/gm, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const buildKnowledgeBaseIndex = (
  sources: { document: KnowledgeBaseDocument; markdown: string }[]
): KnowledgeBaseIndexEntry[] => {
  const entries: KnowledgeBaseIndexEntry[] = []
  for (const { document, markdown } of sources) {
    let heading: string | undefined
    let buffer: string[] = []
    const flush = () => {
      const text = toPlainText(buffer.join('\n'))
      if (heading || text) {
        entries.push({
          document,
          heading,
          anchor: heading ? slugifyHeading(heading) : undefined,
          text,
        })
      }
      buffer = []
    }
    for (const line of markdown.split('\n')) {
      const match = line.match(/^(#{1,5})\s+(.*)$/)
      if (!match) {
        buffer.push(line)
        continue
      }
      flush()
      heading = match[1].length === 1 ? undefined : match[2].trim()
    }
    flush()
  }
  return entries
}

const SNIPPET_LEAD = 50
const SNIPPET_LENGTH = 170

const scoreEntry = (entry: KnowledgeBaseIndexEntry, terms: string[]) => {
  const title = entry.document.title.toLowerCase()
  const heading = (entry.heading ?? '').toLowerCase()
  const text = entry.text.toLowerCase()
  let score = 0
  for (const term of terms) {
    const occurrences = text.split(term).length - 1
    const termScore =
      (title.includes(term) ? 8 : 0) +
      (heading.includes(term) ? 5 : 0) +
      (occurrences > 0 ? 1 + Math.min(3, occurrences - 1) * 0.5 : 0)
    if (termScore === 0) return 0
    score += termScore
  }
  return score
}

export const searchKnowledgeBase = (
  index: KnowledgeBaseIndexEntry[],
  query: string,
  limit = 8
): KnowledgeBaseSearchResult[] => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return []

  const ranked = index
    .map((entry) => ({ entry, score: scoreEntry(entry, terms) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)

  const seen = new Set<string>()
  const results: KnowledgeBaseSearchResult[] = []
  for (const { entry } of ranked) {
    const key = entry.anchor
      ? `${entry.document.path}#${entry.anchor}`
      : entry.document.path
    if (seen.has(key)) continue
    seen.add(key)
    const position = Math.max(0, entry.text.toLowerCase().indexOf(terms[0]))
    const start = Math.max(0, position - SNIPPET_LEAD)
    results.push({
      key,
      document: entry.document,
      heading: entry.heading,
      snippet: `${start > 0 ? '…' : ''}${entry.text.slice(start, start + SNIPPET_LENGTH)}`,
      terms,
    })
    if (results.length >= limit) break
  }
  return results
}

export const splitHighlights = (
  text: string,
  terms: string[]
): { text: string; match: boolean }[] => {
  const escaped = terms
    .filter(Boolean)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  if (escaped.length === 0) return [{ text, match: false }]
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi')
  return text
    .split(pattern)
    .filter(Boolean)
    .map((part) => ({
      text: part,
      match: terms.some((term) => term.toLowerCase() === part.toLowerCase()),
    }))
}
